import * as React from "react"
import Link from "next/link"

import { cva, type VariantProps } from "class-variance-authority"
import { ChevronRight } from "lucide-react"

import { cn } from "../../cn"

/* ── CVA variants ─────────────────────────────────────────── */

const breadcrumbListVariants = cva([
  "flex flex-wrap items-center gap-1.5 text-sm text-coval-text-muted",
])

const breadcrumbItemVariants = cva(["inline-flex items-center gap-1.5"])

const breadcrumbLinkVariants = cva([
  "cursor-pointer text-coval-text-muted transition-colors duration-150",
  "hover:text-coval-text",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active focus-visible:ring-offset-1 focus-visible:rounded-sm",
])

const breadcrumbPageVariants = cva(["text-coval-text font-semibold"])

const breadcrumbSeparatorVariants = cva([
  "text-coval-text-muted [&>svg]:h-3.5 [&>svg]:w-3.5",
])

/* ── Breadcrumb (nav root) ───────────────────────────────── */

export type BreadcrumbProps = React.ComponentPropsWithoutRef<"nav">

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ ...props }, ref) => <nav ref={ref} aria-label="Breadcrumb" {...props} />,
)

Breadcrumb.displayName = "Breadcrumb"

/* ── BreadcrumbList ──────────────────────────────────────── */

export interface BreadcrumbListProps
  extends
    React.ComponentPropsWithoutRef<"ol">,
    VariantProps<typeof breadcrumbListVariants> {}

const BreadcrumbList = React.forwardRef<HTMLOListElement, BreadcrumbListProps>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(breadcrumbListVariants(), className)}
      {...props}
    />
  ),
)

BreadcrumbList.displayName = "BreadcrumbList"

/* ── BreadcrumbItem ──────────────────────────────────────── */

export interface BreadcrumbItemProps
  extends
    React.ComponentPropsWithoutRef<"li">,
    VariantProps<typeof breadcrumbItemVariants> {}

const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(breadcrumbItemVariants(), className)}
      {...props}
    />
  ),
)

BreadcrumbItem.displayName = "BreadcrumbItem"

/* ── BreadcrumbLink ──────────────────────────────────────── */

/**
 * The navigation props a call site may need from `next/link`. The rest of the
 * surface is a plain anchor's, so an `href` stays a `string` rather than
 * widening to Link's `Url`.
 */
type BreadcrumbLinkNavigationProps = Partial<
  Pick<
    React.ComponentPropsWithoutRef<typeof Link>,
    "prefetch" | "replace" | "scroll"
  >
>

export type BreadcrumbLinkProps = React.ComponentPropsWithoutRef<"a"> &
  BreadcrumbLinkNavigationProps &
  VariantProps<typeof breadcrumbLinkVariants>

/**
 * Breadcrumbs are in-app navigation, so an `href` routes through the Next
 * router. A bare `<a>` triggers a full document load, which remounts the app
 * shell and throws away the query cache. Call sites that pass no `href` — the
 * dashboard's in-page drill-down crumbs — stay a plain anchor.
 *
 * Prefetch is off by default, which suppresses the viewport prefetch only. The
 * proxy matches essentially every route, so a breadcrumb prefetch is an
 * authenticated data round-trip rather than a static chunk, and breadcrumbs sit
 * in the viewport on every page — otherwise every crumb would fetch on load
 * whether or not anyone clicks, and the parent paths `DefaultPageHeader`
 * synthesises do not all resolve.
 *
 * Link still prefetches on hover and touch whatever this prop says, and bypasses
 * its own dedupe when it does, so a pointer swept along a trail costs one
 * request per crumb per pass. Its handlers run after any we pass, so a call site
 * cannot suppress that. Pass `prefetch` to restore the load-time prefetch.
 */
const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ className, href, prefetch = false, replace, scroll, ...props }, ref) => {
    const classes = cn(breadcrumbLinkVariants(), className)

    if (href === undefined) {
      // eslint-disable-next-line jsx-a11y/anchor-has-content -- content provided via children spread
      return <a ref={ref} className={classes} {...props} />
    }

    return (
      <Link
        ref={ref}
        href={href}
        prefetch={prefetch}
        replace={replace}
        scroll={scroll}
        className={classes}
        {...props}
      />
    )
  },
)

BreadcrumbLink.displayName = "BreadcrumbLink"

/* ── BreadcrumbPage ──────────────────────────────────────── */

export interface BreadcrumbPageProps
  extends
    React.ComponentPropsWithoutRef<"span">,
    VariantProps<typeof breadcrumbPageVariants> {}

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn(breadcrumbPageVariants(), className)}
      {...props}
    />
  ),
)

BreadcrumbPage.displayName = "BreadcrumbPage"

/* ── BreadcrumbSeparator ─────────────────────────────────── */

export interface BreadcrumbSeparatorProps
  extends
    React.ComponentPropsWithoutRef<"li">,
    VariantProps<typeof breadcrumbSeparatorVariants> {}

const BreadcrumbSeparator = React.forwardRef<
  HTMLLIElement,
  BreadcrumbSeparatorProps
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    role="presentation"
    aria-hidden="true"
    className={cn(breadcrumbSeparatorVariants(), className)}
    {...props}
  >
    <ChevronRight />
  </li>
))

BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

/* ── Exports ─────────────────────────────────────────────── */

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  breadcrumbListVariants,
  breadcrumbItemVariants,
  breadcrumbLinkVariants,
  breadcrumbPageVariants,
  breadcrumbSeparatorVariants,
}
